import { IAppContext } from 'App/AppContext';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { INavItemDefinition, NavGate, NavGroupCaptionId, NavItemId } from './navModel';

//
// The catalogue: every item the main menu can show, with its label, icon, route, the location
// segments it owns and its full visibility rule. navLayout.ts decides where an item sits; this file
// decides what it is and who may see it, so an item can move between groups without ever reaching
// the wrong audience.
//
// A hook rather than a plain table: the translation scanner counts a key as used only when it is
// spelled out inside a t() call, so every label is written out here.
//

/**
 * The licensing gate on the scrolling body of the menu: a signed-in, licensed user of a licensed
 * organization who has finished onboarding.
 */
export const isBodyVisible: NavGate = (appContext) =>
  appContext.isAuthenticated &&
  appContext.user.login.tenantLicensed &&
  appContext.user.login.userLicensed &&
  appContext.isOnboardingInProgress === false &&
  hasUserFeature(appContext, FeatureTypes.Dashboard);

/**
 * The gate on the pinned administration area. Wider than the body gate: an admin whose trial or
 * subscription ended keeps this area so they can subscribe again.
 */
export const isAdminAreaVisible: NavGate = (appContext) =>
  appContext.isAuthenticated &&
  appContext.isOnboardingInProgress === false &&
  (appContext.user.login.userLicensed ||
    (appContext.user.login.tenantId.length > 0 &&
      appContext.user.login.userLicensed === false &&
      appContext.user.login.tenantLicensed === false));

/**
 * The administration area outside the hold of a backup restore: the wrapper of every pinned page except
 * Backup. While a restore holds the organization the API opens only the reads of Backup.
 */
export const isAdminAreaOpen: NavGate = (appContext) =>
  isAdminAreaVisible(appContext) && !appContext.user.login.isHeldByRestore();

/** The pages of the Administration group that need the Admin feature. The header has a wider rule. */
export const isAdminPageVisible: NavGate = (appContext) =>
  isAdminAreaOpen(appContext) && hasUserFeature(appContext, FeatureTypes.Admin);

/** The Tenant Admin group, header and pages. */
export const isTenantAdminAreaVisible: NavGate = (appContext) =>
  isAdminAreaOpen(appContext) && hasUserFeature(appContext, FeatureTypes.TenantAdmin);

//also for a Security Reviewer, who reads the policies, the audit record and the users there
const isAdminHeaderVisible: NavGate = (appContext: IAppContext) =>
  isAdminAreaVisible(appContext) &&
  (hasUserFeature(appContext, FeatureTypes.Admin) ||
    hasUserFeature(appContext, FeatureTypes.AdminAuth) ||
    hasUserFeature(appContext, FeatureTypes.AdminSubscription) ||
    hasUserFeature(appContext, FeatureTypes.PolicyReader) ||
    hasUserFeature(appContext, FeatureTypes.AuditReader) ||
    hasUserFeature(appContext, FeatureTypes.UserReader));

// The literal t() caption of each NavGroupCaptionId; empty while no group has a caption
export const useNavGroupCaptions = (): Record<NavGroupCaptionId, string> => {
  return useMemo(() => ({}), []);
};

export const useNavItems = (): Record<NavItemId, INavItemDefinition> => {
  const { t } = useTranslation(['nav']);

  return useMemo(() => {
    const items: Record<NavItemId, INavItemDefinition> = {
      start: {
        id: 'start',
        label: t('nav:Main.Start'),
        iconName: 'Home',
        className: 'skillcontrol-nav-main-start',
        route: '/',
        matchSegments: [],
        isVisible: () => true,
      },
      history: {
        id: 'history',
        label: t('nav:Main.History'),
        iconName: 'History',
        className: 'skillcontrol-nav-main-history',
        route: '/history',
        matchSegments: [['history']],
        isVisible: isBodyVisible,
      },
      skills: {
        id: 'skills',
        label: t('nav:Main.Skills'),
        iconName: 'Skills',
        className: 'skillcontrol-nav-main-skills',
        route: '/skills',
        matchSegments: [['skills']],
        //every licensed user reads the published Skills of their audience; drafts are filtered by the API
        isVisible: isBodyVisible,
      },
      approvals: {
        id: 'approvals',
        label: t('nav:Main.Approvals'),
        iconName: 'Approvals',
        className: 'skillcontrol-nav-main-approvals',
        route: '/approvals',
        matchSegments: [['approvals']],
        //every licensed user requests approvals with their runs and may be an approver; the API filters the queue
        isVisible: isBodyVisible,
        badge: 'pendingApprovals',
      },
      //
      // Pinned: Administration
      //
      admin: {
        id: 'admin',
        label: t('nav:Admin.Title'),
        iconName: 'Settings',
        className: 'skillcontrol-nav-main-admin',
        matchSegments: [],
        isVisible: isAdminHeaderVisible,
      },
      'admin-integrations': {
        id: 'admin-integrations',
        label: t('nav:Admin.Integrations'),
        iconName: 'Integrations',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/integrations',
        matchSegments: [['admin', 'integrations']],
        isVisible: isAdminPageVisible,
      },
      'admin-capabilities': {
        id: 'admin-capabilities',
        label: t('nav:Admin.Capabilities'),
        iconName: 'Capabilities',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/capabilities',
        matchSegments: [['admin', 'capabilities']],
        isVisible: isAdminPageVisible,
      },
      'admin-library': {
        id: 'admin-library',
        label: t('nav:Admin.Library'),
        iconName: 'DocumentLibrary',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/library',
        matchSegments: [['admin', 'library']],
        //the organization's documents and folders for Skills (phase 28): administrators curate them
        isVisible: isAdminPageVisible,
      },
      'admin-marketplace': {
        id: 'admin-marketplace',
        label: t('nav:Admin.Marketplace'),
        iconName: 'Marketplace',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/marketplace',
        matchSegments: [['admin', 'marketplace']],
        isVisible: isAdminPageVisible,
      },
      'admin-publishing': {
        id: 'admin-publishing',
        label: t('nav:Admin.Publishing'),
        iconName: 'Publishing',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/publishing',
        matchSegments: [['admin', 'publishing']],
        //an organization a REDLAB operator flagged as a marketplace publisher, as its pivot
        isVisible: (appContext) => isAdminPageVisible(appContext) && appContext.user.login.isPublisher,
      },
      'admin-models': {
        id: 'admin-models',
        label: t('nav:Admin.Models'),
        iconName: 'Models',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/models',
        matchSegments: [['admin', 'models']],
        //the organization's own AI models: administrators only, like every ai/models endpoint
        isVisible: isAdminPageVisible,
      },
      'admin-policies': {
        id: 'admin-policies',
        label: t('nav:Admin.Policies'),
        iconName: 'Policies',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/policies',
        matchSegments: [['admin', 'policies']],
        //administrators write the policies, Security Reviewers read them
        isVisible: (appContext) => isAdminAreaOpen(appContext) && hasUserFeature(appContext, FeatureTypes.PolicyReader),
      },
      'admin-groups': {
        id: 'admin-groups',
        label: t('nav:Admin.Groups'),
        iconName: 'Groups',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/groups',
        matchSegments: [['admin', 'groups']],
        isVisible: isAdminPageVisible,
      },
      'admin-audit': {
        id: 'admin-audit',
        label: t('nav:Admin.Audit'),
        iconName: 'Audit',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/audit',
        matchSegments: [['admin', 'audit']],
        //administrators and Security Reviewers read the runtime audit record; nobody writes it
        isVisible: (appContext) => isAdminAreaOpen(appContext) && hasUserFeature(appContext, FeatureTypes.AuditReader),
      },
      'admin-backup': {
        id: 'admin-backup',
        label: t('nav:Admin.Backup'),
        iconName: 'Backup',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/backup',
        matchSegments: [['admin', 'backup']],
        //also while a backup restore holds the organization, so an admin can follow or recover it
        isVisible: (appContext) => isAdminAreaVisible(appContext) && hasUserFeature(appContext, FeatureTypes.Admin),
      },
      'admin-users': {
        id: 'admin-users',
        label: t('nav:Admin.Users'),
        iconName: 'Users',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/users',
        matchSegments: [['admin', 'users']],
        //administrators change roles and login, Security Reviewers read the users
        isVisible: (appContext) => isAdminAreaOpen(appContext) && hasUserFeature(appContext, FeatureTypes.UserReader),
      },
      'admin-subscription': {
        id: 'admin-subscription',
        label: t('nav:Admin.Subscription'),
        iconName: 'Subscription',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/subscription',
        matchSegments: [['admin', 'subscription']],
        isVisible: (appContext) =>
          isAdminAreaOpen(appContext) && hasUserFeature(appContext, FeatureTypes.AdminSubscription),
      },
      'admin-a2a': {
        id: 'admin-a2a',
        label: t('nav:Admin.A2A'),
        iconName: 'AgentToAgent',
        className: 'skillcontrol-nav-main-admin',
        route: '/admin/a2a',
        matchSegments: [['admin', 'a2a']],
        //the organization's A2A opt-in (phase 24): administrators only, like settings/a2a
        isVisible: isAdminPageVisible,
      },

      //
      // Pinned: Tenant Admin. Internal scenes, English labels by design (AGENTS.md).
      //
      tenantadmin: {
        id: 'tenantadmin',
        label: 'Tenant Admin',
        iconName: 'TenantAdmin',
        className: 'skillcontrol-nav-main-tenantadmin',
        matchSegments: [],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-manage': {
        id: 'tenantadmin-manage',
        label: 'Tenants',
        iconName: 'Tenants',
        className: 'skillcontrol-nav-main-tenantadmin',
        route: '/tenant/manage',
        matchSegments: [['tenant', 'manage']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-identity-issuers': {
        id: 'tenantadmin-identity-issuers',
        label: 'Identity issuers',
        iconName: 'IdentityIssuers',
        className: 'skillcontrol-nav-main-tenantadmin',
        route: '/tenant/identity-issuers',
        matchSegments: [['tenant', 'identity-issuers']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-subscription-types': {
        id: 'tenantadmin-subscription-types',
        label: 'Subscription types',
        iconName: 'SubscriptionTypes',
        className: 'skillcontrol-nav-main-tenantadmin',
        route: '/tenant/subscription-types',
        matchSegments: [['tenant', 'subscription-types']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-marketplace': {
        id: 'tenantadmin-marketplace',
        label: 'Marketplace',
        iconName: 'Marketplace',
        className: 'skillcontrol-nav-main-tenantadmin',
        route: '/tenant/marketplace',
        matchSegments: [['tenant', 'marketplace']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-capability-catalog': {
        id: 'tenantadmin-capability-catalog',
        label: 'Capability catalog',
        iconName: 'CapabilityCatalog',
        className: 'skillcontrol-nav-main-tenantadmin',
        route: '/tenant/capability-catalog',
        matchSegments: [['tenant', 'capability-catalog']],
        isVisible: isTenantAdminAreaVisible,
      },
      'tenantadmin-ai-credits': {
        id: 'tenantadmin-ai-credits',
        label: 'AI credits',
        iconName: 'AICredits',
        className: 'skillcontrol-nav-main-tenantadmin',
        route: '/tenant/ai-credits',
        matchSegments: [['tenant', 'ai-credits']],
        isVisible: isTenantAdminAreaVisible,
      },
    };

    return items;
  }, [t]);
};
